import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api/client.js';

// Pending counts for the admin rail's queue badges. Lives in a context because
// both AdminShell (badges) and CommandDeck (the Access/Design portals) want the
// same numbers, and there's no reason to fetch them twice.
//
// Refetches on pathname change: deciding a request navigates or re-renders, and
// a stale badge on an emptied queue is worse than no badge. Failures are
// swallowed to null — a badge is decoration, it must never surface an error or
// block the page it decorates.
const AdminQueueContext = createContext({
  accessPending: null,
  designPending: null,
  refresh: () => {},
});

export function AdminQueueProvider({ children }) {
  const { pathname } = useLocation();
  const [counts, setCounts] = useState({ accessPending: null, designPending: null });

  const refresh = useCallback(async () => {
    const [access, design] = await Promise.all([
      api.adminListAccessRequests('pending').catch(() => null),
      api.adminListDesignRequests('pending').catch(() => null),
    ]);
    setCounts({
      accessPending: access ? (access.requests ?? []).length : null,
      designPending: design ? (design.requests ?? []).length : null,
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh, pathname]);

  const value = useMemo(() => ({ ...counts, refresh }), [counts, refresh]);

  return (
    <AdminQueueContext.Provider value={value}>
      {children}
    </AdminQueueContext.Provider>
  );
}

export function useAdminQueues() {
  return useContext(AdminQueueContext);
}
